// ===================================================
// Toast.js — 액션 결과 피드백 토스트 메시지
//
// 역할:
//   - 저장 완료, 오류 등 액션 결과를 3초간 하단에 표시
//   - 디자인 규칙: 하단 요소(GNB) 위 10px에 위치
//   - 배경 터치 가능 (딤 처리 없음)
//   - 탭하면 즉시 사라짐
// ===================================================

import { useState, useEffect, useRef } from 'react';

// onNavigate: 제공되면 클릭 시 해당 함수를 호출하고 토스트를 닫음 (채팅 이동 등)
function Toast({ message, type = 'success', onDone, onNavigate }) {
  const [visible, setVisible] = useState(false);
  // onDone이 매 렌더마다 새로 생성돼도 최신 버전을 참조하기 위해 ref 사용
  const onDoneRef = useRef(onDone);
  const onNavigateRef = useRef(onNavigate);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  useEffect(() => {
    // 마운트 직후 fade-in
    const raf = requestAnimationFrame(() => setVisible(true));

    // 5초 후 자동 사라짐 (onNavigate가 있는 토스트는 좀 더 오래 유지)
    const delay = onNavigate ? 5000 : 3000;
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDoneRef.current(), 280);
    }, delay);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [onNavigate]);

  // 탭하면 즉시 사라짐 (onNavigate가 있으면 해당 함수도 호출)
  const handleClick = () => {
    setVisible(false);
    if (onNavigateRef.current) onNavigateRef.current();
    setTimeout(() => onDoneRef.current(), 280);
  };

  return (
    <div
      className={`toast toast-${type}${visible ? ' toast-visible' : ''}${onNavigate ? ' toast-navigable' : ''}`}
      onClick={handleClick}
    >
      {/* 성공 아이콘: 체크 */}
      {type === 'success' && !onNavigate && (
        <svg className="toast-icon" viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {/* 오류 아이콘: ! */}
      {type === 'error' && (
        <svg className="toast-icon" viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      )}
      <span>{message}</span>
      {/* 이동 가능한 토스트에는 화살표 표시 */}
      {onNavigate && (
        <svg className="toast-icon toast-arrow" viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      )}
    </div>
  );
}

export default Toast;
